import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";

const mougins: ChauffeurPage = {
  slug: "mougins",
  title: "VTC & chauffeur privé à Mougins",
  subtitle:
    "Chauffeur privé à Mougins : village gastronomique, golfs, écoles internationales et résidences au-dessus de Cannes.",
  keywords: [
    "vtc mougins",
    "chauffeur privé mougins",
    "chauffeur mougins village",
  ],
  hero: {
    title: "VTC & chauffeur privé à Mougins",
    subtitle:
      "Le village perché de la gastronomie, juste au-dessus de Cannes : un chauffeur pour le résidentiel, les golfs et les tables.",
    keywords: [
      "VTC Mougins",
      "chauffeur Mougins village",
      "chauffeur golf Mougins",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & chauffeur privé à Mougins",
    badges: ["Au-dessus de Cannes", "Golfs & gastronomie", "School runs"],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Mougins",
    items: [
      {
        title: "Résidentiel haut de gamme",
        description:
          "Domaines et villas dispersés sur les collines : dépose à la grille et chauffeur attitré pour les familles installées à Mougins.",
      },
      {
        title: "Capitale de la gastronomie",
        description:
          "Héritage de Roger Vergé et du Moulin de Mougins, tables étoilées du Vieux Village : aller et retour de dîner sans contrainte.",
      },
      {
        title: "Golfs & art de vivre",
        description:
          "Royal Mougins et parcours voisins, galeries d'art et musée MACM : déplacements souples à la journée.",
      },
      {
        title: "Écoles internationales",
        description:
          "Proximité du Mougins School et des établissements internationaux : trajets école réguliers, ponctuels et sécurisés.",
      },
    ],
  },
  services: {
    heading: "Services chauffeur à Mougins",
    services: [
      {
        title: "Mise à disposition résidentielle",
        text: "Chauffeur attitré pour propriétaires et familles, des collines de Mougins à Cannes, Sophia Antipolis ou l'aéroport.",
      },
      {
        title: "Transferts gastronomiques",
        text: "Tables du Vieux Village et de la plaine : aller et retour de déjeuner ou de dîner, sans contrainte de conduite.",
      },
      {
        title: "Transferts golf",
        text: "Royal Mougins et parcours de la région, avec espace bagages pour les sacs et horaires calés sur les départs.",
      },
      {
        title: "School runs",
        text: "Trajets réguliers vers le Mougins School et les écoles internationales, ponctualité et chauffeurs de confiance.",
      },
      {
        title: "Transferts aéroport & Cannes",
        text: "Mougins ↔ aéroport de Nice avec suivi de vol, et liaison rapide vers la Croisette pour les événements cannois.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Vieux Village de Mougins",
        description: "Village perché, ruelles, galeries d'art et restaurants.",
      },
      {
        name: "Musée MACM",
        description: "Musée d'Art Classique de Mougins, au cœur du village.",
      },
      {
        name: "Royal Mougins Golf",
        description:
          "Parcours et resort, départs et reprises calés sur le jeu.",
      },
      {
        name: "Mougins School",
        description: "École internationale, trajets scolaires réguliers.",
      },
      {
        name: "Domaines résidentiels",
        description:
          "Villas et propriétés sur les collines, dépose à la grille.",
      },
      {
        name: "Mougins-le-Haut",
        description: "Quartier résidentiel moderne au-dessus de la plaine.",
      },
    ],
  },
  generic: {
    heading: "Mougins, le village gastronomique au-dessus de Cannes",
    subheading:
      "Résidentiel, golfs et grandes tables, à quelques minutes de la Croisette et de Sophia Antipolis.",
    paragraphs: [
      "Mougins a bâti sa réputation sur la gastronomie : l'héritage de Roger Vergé et du Moulin de Mougins, les tables étoilées et les galeries d'art du Vieux Village en font une étape de l'art de vivre azuréen. Un chauffeur assure l'aller et le retour de dîner, sans contrainte de conduite après le repas.",
      "Le village est aussi un secteur résidentiel prisé, fait de domaines et de villas dispersés sur les collines, avec ses golfs et ses écoles internationales. Pour les familles installées, un chauffeur attitré simplifie le quotidien : school runs, golf, rendez-vous, sans souci de stationnement ni de coordination.",
      "À quelques minutes de Cannes et de Sophia Antipolis, Mougins sert aussi d'hébergement de repli lors des grands événements cannois, quand la Croisette affiche complet. Le chauffeur relie alors villa et palais des festivals en évitant les engorgements.",
    ],
    bullets: ["Dépose à la grille", "Retour de dîner", "School runs réguliers"],
  },
  process: {
    heading: "Réserver un chauffeur à Mougins",
    steps: [
      "Indiquez vos besoins (villa, golf, école, restaurant, aéroport, Cannes).",
      "Nous calons les horaires sur vos départs golf, services scolaires ou réservations.",
      "Le chauffeur se présente à la grille de la villa ou à l'adresse indiquée.",
      "Trajet direct ou mise à disposition à la journée selon votre programme.",
    ],
  },
  faq: [
    {
      q: "Proposez-vous un chauffeur attitré pour les familles de Mougins ?",
      a: "Oui. Pour les propriétaires et familles installées sur les collines de Mougins, nous proposons une mise à disposition avec chauffeur attitré : school runs, golf, rendez-vous et dîners, avec dépose à la grille.",
    },
    {
      q: "Assurez-vous les trajets scolaires vers le Mougins School ?",
      a: "Oui, nous organisons des school runs réguliers vers le Mougins School et les écoles internationales du secteur, avec des chauffeurs de confiance et une ponctualité adaptée aux horaires de classe.",
    },
    {
      q: "Combien de temps entre Mougins et la Croisette à Cannes ?",
      a: "Comptez une quinzaine de minutes hors heures de pointe. Pendant le Festival et les grands salons, le chauffeur anticipe les engorgements pour relier votre villa au palais des festivals à l'heure.",
    },
    {
      q: "Gérez-vous les transferts golf avec les sacs ?",
      a: "Oui, vers le Royal Mougins et les parcours voisins, avec un véhicule disposant de l'espace bagages nécessaire pour les sacs et des horaires calés sur vos départs.",
    },
  ],
  relatedLinks: [
    {
      label: "Réserver un transfert Cannes Aéroport de Nice",
      href: "/chauffeur/transferts/cannes-aeroport-nice",
    },
    {
      label: "Réserver un transfert Cannes Monaco",
      href: "/chauffeur/transferts/cannes-monaco",
    },
    {
      label: "Toutes les liaisons avec chauffeur",
      href: "/chauffeur/transferts",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Mougins",
    text: "Villa, golf, école ou table étoilée : réservez votre chauffeur attitré à Mougins.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver un chauffeur",
  },
  seo: {
    title:
      "VTC Mougins — chauffeur privé village, golf & écoles | KS Rent Services",
    description:
      "VTC et chauffeur privé à Mougins : village gastronomique, golfs, écoles internationales et résidences au-dessus de Cannes. School runs, aéroport, 24/7.",
    canonical: "/chauffeur/mougins",
  },
};

export default mougins;
